<template>
  <div class="retention-admin-panel">
    <h3>Retention Messaging Service</h3>
    <p class="hint">
      Apple env (sandbox / production) is pinned to this deployment — staging panel manages the sandbox URL,
      production panel manages the production URL. Performance tests are sandbox-only on Apple's side and can
      only be initiated from the staging admin panel.
    </p>

    <div class="card app-picker">
      <header><h4>App</h4></header>
      <div class="body">
        <p class="muted">
          Realtime URL and performance-test state are stored per app (bundle id) on Apple's side and mirrored
          into <code>retention-messaging-configuration</code> for cross-environment visibility.
        </p>
        <select v-model="selectedAppId" class="form-control" @change="onAppChange">
          <option :value="null" disabled>— Select an app —</option>
          <option v-for="(name, appId) in apps" :key="appId" :value="appId">
            {{ name }} ({{ appId }})
          </option>
        </select>
        <p v-if="docEnv" class="muted env-line">
          This panel is configuring <strong>{{ docEnv }}</strong>.
        </p>
      </div>
    </div>

    <div class="card" :class="{ disabled: !selectedAppId }">
      <header>
        <h4>Realtime URL</h4>
        <button v-if="selectedAppId && !urlBusy" class="link" @click="refreshFromApple">
          <i class="fas fa-sync"></i> Refresh from Apple
        </button>
      </header>
      <div class="body">
        <p class="muted" v-if="!selectedAppId">Pick an app above to view or configure its realtime URL.</p>
        <p class="muted" v-else-if="urlState === 'loading'">Loading…</p>
        <template v-else>
          <p v-if="cachedUrl" class="current-url">
            Cached: <code>{{ cachedUrl }}</code>
            <span v-if="cachedUrlFetchedAt" class="muted small"> · last fetched {{ cachedUrlFetchedAt }}</span>
          </p>
          <p v-else class="muted">Not configured for {{ docEnv }}.</p>
        </template>

        <div class="form-row" v-if="selectedAppId">
          <input
            v-model="urlInput"
            class="form-control"
            placeholder="https://your-domain/api/retention/respond"
            type="url"
          />
          <button class="btn btn-primary" :disabled="urlBusy || !urlInput" @click="saveUrl">
            <span v-if="urlBusy" class="spinner-border spinner-border-sm"></span>
            <span v-else>Save</span>
          </button>
        </div>
        <button
          v-if="cachedUrl && selectedAppId"
          class="link danger small"
          :disabled="urlBusy"
          @click="clearUrl"
        >
          Clear configured URL
        </button>
        <p v-if="urlError" class="error">{{ urlError }}</p>
      </div>
    </div>

    <div class="card" :class="{ disabled: !selectedAppId }">
      <header>
        <h4>Performance Test</h4>
        <span v-if="!isStaging" class="muted small">Read-only on prod panel</span>
      </header>
      <div class="body">
        <p class="muted">
          Apple requires a passing sandbox performance test before configuring a production realtime URL.
          <span v-if="isStaging">
            Provide an active sandbox auto-renewable subscription's <code>originalTransactionId</code>.
          </span>
          <span v-else>
            Run new tests from the staging admin panel. The latest <code>requestId</code> is mirrored here so
            you can fetch the result.
          </span>
        </p>

        <div v-if="isStaging" class="form-row">
          <input
            v-model="perfInput"
            class="form-control"
            placeholder="originalTransactionId (sandbox)"
            :disabled="!selectedAppId"
          />
          <button
            class="btn btn-primary"
            :disabled="!selectedAppId || perfBusy || !perfInput"
            @click="runPerfTest"
          >
            <span v-if="perfBusy" class="spinner-border spinner-border-sm"></span>
            <span v-else>Run Test</span>
          </button>
        </div>
        <p v-if="perfError" class="error">{{ perfError }}</p>

        <div v-if="storedRequestId" class="perf-run">
          <p>
            Test request: <code>{{ storedRequestId }}</code>
            <span v-if="perfStartedAt" class="muted small"> · started {{ perfStartedAt }}</span>
          </p>
          <button class="link" :disabled="resultBusy" @click="fetchResult(storedRequestId)">
            <i class="fas fa-sync"></i>
            <span v-if="resultBusy">Polling…</span>
            <span v-else>Refresh result</span>
          </button>
          <pre v-if="perfResult">{{ formatResult(perfResult) }}</pre>
        </div>
        <p v-else-if="selectedAppId" class="muted small">No performance test recorded for this app yet.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { retentionService } from '../../services/retention.service';

const formatTimestamp = (ts) => {
  if (!ts) return null;
  // Firestore Timestamp serialization: { _seconds, _nanoseconds } or { seconds, nanoseconds }
  const seconds = ts._seconds ?? ts.seconds ?? null;
  if (seconds == null) return null;
  return new Date(seconds * 1000).toLocaleString();
};

export default {
  name: 'RetentionAdminPanel',
  data() {
    return {
      selectedAppId: null,

      docEnv: null,
      appConfig: null,

      urlState: 'idle',
      urlInput: '',
      urlBusy: false,
      urlError: null,

      perfInput: '',
      perfBusy: false,
      perfError: null,
      perfResult: null,
      resultBusy: false
    };
  },
  computed: {
    ...mapGetters(['apps', 'isStaging']),
    envBlock() {
      if (!this.appConfig || !this.docEnv) return null;
      return this.appConfig[this.docEnv] || null;
    },
    cachedUrl() {
      return this.envBlock?.realtimeUrl?.value || null;
    },
    cachedUrlFetchedAt() {
      return formatTimestamp(this.envBlock?.realtimeUrl?.lastFetchedAt);
    },
    storedRequestId() {
      return this.appConfig?.staging?.perfTest?.requestId || null;
    },
    perfStartedAt() {
      return formatTimestamp(this.appConfig?.staging?.perfTest?.startedAt);
    }
  },
  methods: {
    async onAppChange() {
      this.urlInput = '';
      this.urlError = null;
      this.perfInput = '';
      this.perfError = null;
      this.perfResult = null;
      await this.loadConfiguration();
    },
    async loadConfiguration() {
      if (!this.selectedAppId) {
        this.appConfig = null;
        this.docEnv = null;
        return;
      }
      this.urlState = 'loading';
      try {
        const res = await retentionService.getRetentionConfiguration(this.selectedAppId);
        const result = res.data?.result || {};
        this.docEnv = result.env || null;
        this.appConfig = result.config || null;
        this.urlInput = this.cachedUrl || '';
      } catch (err) {
        this.urlError = err.response?.data?.message || err.message;
      } finally {
        this.urlState = 'idle';
      }
    },
    async refreshFromApple() {
      if (!this.selectedAppId) return;
      this.urlState = 'loading';
      this.urlError = null;
      try {
        await retentionService.getRealtimeUrl(this.selectedAppId);
        await this.loadConfiguration();
      } catch (err) {
        this.urlError = err.response?.data?.message || err.message;
        this.urlState = 'idle';
      }
    },
    async saveUrl() {
      if (!this.selectedAppId) return;
      this.urlBusy = true;
      this.urlError = null;
      try {
        await retentionService.configureRealtimeUrl(this.selectedAppId, { realtimeURL: this.urlInput });
        await this.loadConfiguration();
      } catch (err) {
        this.urlError = err.response?.data?.message || err.message;
      } finally {
        this.urlBusy = false;
      }
    },
    async clearUrl() {
      if (!this.selectedAppId) return;
      if (!confirm('Clear the configured realtime URL? Apple will fall back to defaults until a new URL is set.')) return;
      this.urlBusy = true;
      this.urlError = null;
      try {
        await retentionService.deleteRealtimeUrl(this.selectedAppId);
        this.urlInput = '';
        await this.loadConfiguration();
      } catch (err) {
        this.urlError = err.response?.data?.message || err.message;
      } finally {
        this.urlBusy = false;
      }
    },
    async runPerfTest() {
      if (!this.selectedAppId || !this.isStaging) return;
      this.perfBusy = true;
      this.perfError = null;
      this.perfResult = null;
      try {
        await retentionService.runPerformanceTest(this.selectedAppId, {
          originalTransactionId: this.perfInput
        });
        this.perfInput = '';
        await this.loadConfiguration();
      } catch (err) {
        this.perfError = err.response?.data?.message || err.message;
      } finally {
        this.perfBusy = false;
      }
    },
    async fetchResult(requestId) {
      if (!this.selectedAppId || !requestId) return;
      this.resultBusy = true;
      this.perfError = null;
      try {
        const res = await retentionService.getPerformanceTestResult(this.selectedAppId, requestId);
        this.perfResult = res.data?.result || null;
        // Update lastCheckedAt timestamp in cache
        await this.loadConfiguration();
      } catch (err) {
        this.perfError = err.response?.data?.message || err.message;
      } finally {
        this.resultBusy = false;
      }
    },
    formatResult(result) {
      try {
        return JSON.stringify(result, null, 2);
      } catch (_e) {
        return String(result);
      }
    }
  }
};
</script>

<style scoped>
.retention-admin-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 720px;
}

h3 { margin: 0 0 0.25rem; color: #2c3e50; }

.hint {
  color: #6c757d;
  font-size: 0.85rem;
  margin: 0 0 0.5rem;
}

.env-line {
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

.env-line strong {
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.card.disabled {
  opacity: 0.6;
}

.card.app-picker {
  border-color: #3498db;
}

.card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.card header h4 {
  margin: 0;
  font-size: 1rem;
  color: #2c3e50;
}

.card .body {
  padding: 1rem;
}

.muted { color: #6c757d; font-size: 0.9rem; }

.muted.small { font-size: 0.75rem; }

.current-url {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: #2c3e50;
}

.current-url code {
  word-break: break-all;
  background: #f1f3f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.form-row {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  margin-top: 0.5rem;
}

.retention-admin-panel .form-control {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  background: #fff;
  color: #2c3e50;
}

.retention-admin-panel .form-control::placeholder {
  color: #adb5bd;
  opacity: 1;
}

.retention-admin-panel .form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.retention-admin-panel select.form-control {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='%236c757d' d='M6 8L0 0h12z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}

.btn {
  padding: 0.5rem 1.1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-primary {
  background: #3498db;
  color: #fff;
}

.btn-primary:disabled {
  background: #a4cef0;
  cursor: not-allowed;
}

.link {
  background: none;
  border: none;
  color: #3498db;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.85rem;
}

.link:hover { background: #eef6fc; }

.link.danger { color: #c0392b; }
.link.danger:hover { background: #fcecea; }

.link.small { font-size: 0.75rem; margin-top: 0.5rem; display: inline-block; }

.error {
  margin-top: 0.5rem;
  color: #c0392b;
  font-size: 0.85rem;
}

.perf-run {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #dee2e6;
  color: #2c3e50;
}

.retention-admin-panel .perf-run p {
  color: #2c3e50;
}

.retention-admin-panel .perf-run pre {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 0.8rem;
  max-height: 220px;
  overflow: auto;
  color: #2c3e50;
}
</style>
